import UtilSP from 'SATPROF_UTILS/UtilSP';
import Complex from 'SATPROF_UTILS/Complex';
import SigProc from 'SATPROF_UTILS/SigProc';
import Distortion from 'SATPROF_UTILS/Distortion';

export default class Waveform2 {
/*
Contains a digital waveform with parameters defining number of points, center freq, freq step,
impedance.
Order is always increasing freq (not FFT)
Has functions to convert between freq and time domain, power/phase, and I-Q

Methods:
powerSpectrumOnly:boolean
    If true, only maintains and operates on the power spectrum.
    If false, maintains and operates on the complex time and freq arrays.

clone(w:Waveform2)	

setToConstant( valRe:number, valIm:number, mode:string); //"freqDomainIQ" "timeDomainIQ"
    sets freq or time re and im arrays to constant valRe and valIm

addGain( gaindB, units:string) // Units: "dB" 
    adds gain to freq or time re and im arrays

applyHPA3Distortion(ss_gain:number, psat_dbm:number)
    Applies HPA_model3 distortion to the current waveform. Evaluates only time domain power dBm and phase deg arrays.

applyScalarFilter(filter:Array<number>)  
    Multiplies frequency domain waveform by filter number array
    Validates frequency arrays
    Invalidates time arrays

addWaveform(w:Waveform2) 
    Sums in another waveform. powerSpectrumOnly must match.
    Automatically takes into account time and freq validities and returns whichever validities it can

addNoise(val:number, units:string, mode:string); // 
    Sums in random gaussian noise to the current freq re and im arrays
	Units: "noisePowerDensitydBmHz" "NFdB" "noiseTempK" 
	mode: "freqDomain" MANDATORY
	If waveform freqDomainComplexValid property is not valid, this method creates noise in the freq domain re/im.

computeAll()
    Regenerates all arrays, provided enough info is available.
    
computeAllFromTime()
    Completes freq response from time (either complex or pwr/phase)

computeAllFromFreq()
    Regenerates time response from freq (either complex or pwr/phase)



convertTimeComplex2FreqComplex(putInFreqOrder:boolean = true). 
    Generates freq re and im arrays from time re and im arrays.
convertFreqComplex2TimeComplex()
    Generates time re and im arrays from freq re and im arrays.
convertFreqComplex2PowerPhase
convertTimeComplex2PowerPhase

everythingIsInvalid()
everythingIsValid()

Properties:
N				number of points
freqDomainIsInFreqOrder
impedance
Fc
Fstep

These properties are all Array<number>, read/write.  Must have length N.  Req arrays are in Freq order (not FFT order)
freqDomainRe	
freqDomainIm	
timeDomainRe
timeDomainIm
timeDomainPwrdBm
timeDomainPhaseDeg
freqDomainPwrdBm		
freqDomainPhaseDeg
These properties are Array<Complex>, read/write
freqDomainComplex
timeDomainComplex

These properties are all boolean, read-only.
freqDomainComplexValid 
freqDomainPwrPhaseValid
timeDomainComplexValid 
timeDomainPwrPhaseValid

status      R/O  Boolean of valid parameters
printWaveform()	
averagePower_dBm
	
*/
    private _freqDomainComplex:Array<Complex>; 
    private _freqDomainComplexValid:boolean = false;

    private _timeDomainComplex:Array<Complex>; 
    private _timeDomainComplexValid:boolean = false;

    private _freqDomainPwrdBm:Array<number>; 
    private _freqDomainPhaseDeg:Array<number>; 
    private _freqDomainPwrPhaseValid:boolean = false;
    
    private _timeDomainPwrdBm:Array<number>; 
    private _timeDomainPhaseDeg:Array<number>;
    private _timeDomainPwrPhaseValid:boolean = false;

    private _impedance:number = 50;
    private _N_global = 0;
    private _Fc_global = 1e9; // Center frequency of the spectrum
    private _Fstep_global = 1e3; // Frequency step size of spectrum points
    private _powerSpectrumOnly:boolean = false;

    private _distortionShape:string = "SSPA";
    private _HPA3_softness:number = 0.8; //softness of the compression curve.  0 = ideal limiter, 1.5 = typical TWT. 
    private _HPA3_IBO_corner_dB:number = 0;
    private _HPA3_gainBumpHeight_dB:number = 1; 
    private _HPA3_gainBumpWidth_dB:number = 3; 
    private _HPA3_IBOgainBump_dB:number = -4; 
    private _HPA3_gainFoldover:number = 0; 
    private _HPA3_phaseMax_deg:number = 33; 
    private _HPA3_IBOphasePeak_dB:number = 8.1; 
    private _HPA3_phaseWidth_dB:number = 10; 
    private _HPA3_phaseShape:number = 2; 

    constructor(ng:number, fc:number, fstep:number){
        this._N_global = ng;
        this._Fc_global = fc;
        this._Fstep_global = fstep;
        //this.distortionShape = "SSPA"; // use setter

        this.zeroWaveform();
    }

    public computeAll(){
        /*
        console.log("In computeAll, "+
        "\n  _timeDomainComplexValid = "+this._timeDomainComplexValid+
        "\n  _freqDomainComplexValid = "+this._freqDomainComplexValid+
        "\n  _timeDomainPwrPhaseValid = "+this._timeDomainPwrPhaseValid+
        "\n  _freqDomainPwrPhaseValid = "+this._freqDomainPwrPhaseValid
    
        */
        /*
        Status
        Case tdComplexValid  tdPwrPhaseValid  fdComplexValid  fdPwrPhaseValid    Action
        1. False            False           False            False             ERROR - no valid data
        2. False            False           False            True              fdp->fdc, ifft, tdc->tdp
        3. False            False           True             False             fdc->fdp, ifft, tdc->tdp
        4. False            False           True             True              ifft, tdc->tdp
        5. False            True            False            False             tdp->tdc, fft, fdc->fdp
        6. False            True            False            True              tdp->tdc, fdp->fdc
        7. False            True            True             False             tdp->tdc, fdc->fdp
        8. False            True            True             True              tdp->tdc
        9. True            False           False            False              tdc->tdp, fft, fdc->fdp
        10.True            False           False            True               tdc->tdp, fdp->fdc
        11.True            False           True             False              tdc->tdp, fdc->fdp
        12.True            False           True             True               tdc->tdp 
        13.True            True            False            False              fft, fdc->fdp
        14.True            True            False            True               fdp->fdc
        15.True            True            True             False              fdc->fdp
        16.True            True            True             True               No action required

        */


        if (this._powerSpectrumOnly){
            // Nothing to do since the only array used is _freqDomainPwrdBm
            return;
        }
        let td_c_valid:boolean = this._timeDomainComplexValid;
        let td_p_valid:boolean = this._timeDomainPwrPhaseValid;
        let fd_c_valid:boolean = this._freqDomainComplexValid;
        let fd_p_valid:boolean = this._freqDomainPwrPhaseValid;
        if (!td_c_valid && !td_p_valid && !fd_c_valid && !fd_p_valid){  // case 1
            console.log("ERROR in Waveform2 computeAll(). No valid data to use.");
        }else if (!td_c_valid && !td_p_valid && !fd_c_valid && fd_p_valid){  // case 2
            this.convertFreqPowerPhase2Complex(); // fdp->fdc
            this.convertFreqComplex2TimeComplex(); // ifft
            this.convertTimeComplex2PowerPhase(); //tdc->tdp
        }else if (!td_c_valid && !td_p_valid && fd_c_valid && !fd_p_valid){  // case 3
            this.convertFreqComplex2PowerPhase(); //fdc->fdp
            this.convertFreqComplex2TimeComplex(); // ifft
            this.convertTimeComplex2PowerPhase(); //tdc->tdp
        }else if (!td_c_valid && !td_p_valid && fd_c_valid && fd_p_valid){  // case 4
            this.convertFreqComplex2TimeComplex(); // ifft
            this.convertTimeComplex2PowerPhase(); //tdc->tdp
        }else if (!td_c_valid && td_p_valid && !fd_c_valid && !fd_p_valid){  // case 5
            this.convertTimePowerPhase2Complex(); // tdp->tdc
            this.convertTimeComplex2FreqComplex(); // Do fwd FFT
            this.convertFreqComplex2PowerPhase(); //fdc->fdp
        }else if (!td_c_valid && td_p_valid && !fd_c_valid && fd_p_valid){  // case 6
            this.convertTimePowerPhase2Complex(); // tdp->tdc
            this.convertFreqPowerPhase2Complex(); // // fdp->fdc
        }else if (!td_c_valid && td_p_valid && fd_c_valid && !fd_p_valid){  // case 7
            this.convertTimePowerPhase2Complex(); // tdp->tdc
            this.convertFreqComplex2PowerPhase(); //fdc->fdp
        }else if (!td_c_valid && td_p_valid && fd_c_valid && fd_p_valid){  // case 8
            this.convertTimePowerPhase2Complex(); // tdp->tdc
        }else if (td_c_valid && !td_p_valid && !fd_c_valid && !fd_p_valid){  // case 9
            this.convertTimeComplex2PowerPhase(); //tdc->tdp
            this.convertTimeComplex2FreqComplex(); // Do fwd FFT
            this.convertFreqComplex2PowerPhase(); //fdc->fdp
        }else if (td_c_valid && !td_p_valid && !fd_c_valid && fd_p_valid){  // case 10
            this.convertTimeComplex2PowerPhase(); //tdc->tdp
            this.convertFreqPowerPhase2Complex(); // // fdp->fdc
        }else if (td_c_valid && !td_p_valid && fd_c_valid && !fd_p_valid){  // case 11
            this.convertTimeComplex2PowerPhase(); //tdc->tdp
            this.convertFreqComplex2PowerPhase(); //fdc->fdp
        }else if (td_c_valid && !td_p_valid && fd_c_valid && fd_p_valid){  // case 12
            this.convertTimeComplex2PowerPhase(); //tdc->tdp
        }else if (td_c_valid && td_p_valid && !fd_c_valid && !fd_p_valid){  // case 13
            this.convertTimeComplex2FreqComplex(); // Do fwd FFT
            this.convertFreqComplex2PowerPhase(); //fdc->fdp
        }else if (td_c_valid && td_p_valid && !fd_c_valid && fd_p_valid){  // case 14
            this.convertFreqPowerPhase2Complex(); // // fdp->fdc
        }else if (td_c_valid && td_p_valid && fd_c_valid && !fd_p_valid){  // case 15
            this.convertFreqComplex2PowerPhase(); //fdc->fdp
        }else if (td_c_valid && td_p_valid && fd_c_valid && fd_p_valid){  // case 16
            // No action required. Everything is already valid
        } else {
            console.log("BUG in Waveform2 computeAll(). FD/TD complex/power combination not found.");
        }

        /*
        if (this._powerSpectrumOnly){
            // Nothing to do since the only array used is _freqDomainPwrdBm
        } else {
            if (this._timeDomainComplexValid && this._freqDomainComplexValid){
                // No need to do any FFTs .
                if (!this._timeDomainPwrPhaseValid){
                    this.convertTimeComplex2PowerPhase();
                }
                if (!this._freqDomainPwrPhaseValid){
                    this.convertFreqComplex2PowerPhase();
                }
            } else if (this._timeDomainComplexValid && !this._freqDomainComplexValid){
                 // Need to do the forward FFT
                this.convertTimeComplex2FreqComplex();
                this.convertFreqComplex2PowerPhase();
                if (!this._timeDomainPwrPhaseValid){
                    this.convertTimeComplex2PowerPhase();
                }               
            }else if (!this._timeDomainComplexValid && this._freqDomainComplexValid){
                // Need to do the inverse FFT
                 this._timeDomainPwrPhaseValid = false;;
                 this.convertFreqComplex2TimeComplex();
                 this.convertTimeComplex2PowerPhase();
                 if (!this._freqDomainPwrPhaseValid){
                    this.convertFreqComplex2PowerPhase();
                }                
            }else if (!this._timeDomainComplexValid && !this._freqDomainComplexValid){
                // Neither of the complex arrays are valid.
                if (this._timeDomainPwrPhaseValid && !this._freqDomainPwrPhaseValid){
                    // Base everything off the time domain pwr and freq arrays.
                    this.convertTimePowerPhase2Complex(); // Convert time power/phase to complex
                    this.convertTimeComplex2FreqComplex(); // Do fwd FFT
                    this.convertFreqComplex2PowerPhase(); // Generate freq power/phase
                } else if (!this._timeDomainPwrPhaseValid && this._freqDomainPwrPhaseValid){
                    this.convertFreqPowerPhase2Complex(); // Convert freq power/phase to complex
                    this.convertFreqComplex2TimeComplex();// Do inverse FFT
                    this.convertTimeComplex2PowerPhase(); // Generate time power/phase
                } else {
                    console.log("ERROR in Waveform2 computeAll(). No valid data to use.");
                }
            }

        }
        */
    }
    public computeAllFromTime(){
            console.log("WARNING in Waveform2.computeAllFromTime: This function may not be working properly! Use computeAll().")

        if (this._powerSpectrumOnly){
            // Nothing to do since the only array used is _freqDomainPwrdBm
            console.log("WARNING in Waveform2.computeAllFromTime: no action because _powerSpectrumOnly = true.")
        } else {
            if ( UtilSP.XOR(!this._timeDomainComplexValid, !this._timeDomainPwrPhaseValid)){
                // Either time complex or time pwrphase is invalid, but not both
                if (!this._timeDomainPwrPhaseValid){
                    this.convertTimeComplex2PowerPhase();
                }
                if (!this._timeDomainComplexValid){
                    this.convertTimePowerPhase2Complex();
                }
            } else {
                console.log("ERROR in Waveform2.computeAllFromTime: no valid time data.");
            }
            this.convertTimeComplex2FreqComplex(); // Do fwd FFT
            this.convertFreqComplex2PowerPhase(); // Generate freq power/phase
            this.everythingIsValid();
        }
    }    
    public computeAllFromFreq(){
        console.log("WARNING in Waveform2.computeAllFromFreq: This function may not be working properly! Use computeAll().")

        if (this._powerSpectrumOnly){
            // Nothing to do since the only array used is _freqDomainPwrdBm
            console.log("WARNING in Waveform2.computeAllFromFreq: no action because _powerSpectrumOnly = true.")
        } else {
            if ( UtilSP.XOR(!this._freqDomainComplexValid, !this._freqDomainPwrPhaseValid)){
                if (!this._freqDomainPwrPhaseValid){
                    this.convertFreqComplex2PowerPhase();
                }
                if (!this._freqDomainComplexValid){
                    this.convertFreqPowerPhase2Complex();
                }
            } else {
                console.log("ERROR in Waveform2.computeAllFromFreq: no valid freq data.");
            }
            this.convertFreqComplex2TimeComplex(); // Do fwd FFT
            this.convertTimeComplex2PowerPhase(); // Generate time power/phase
            this.everythingIsValid();
        }
    }  
    public get averagePower_dBm():number{
        this.computeAll();
        let pCum:number = 0;
        let i:number;
        for (i=0; i<this._N_global; i++){
            pCum += UtilSP.dBm2W(this.timeDomainPwrdBm[i])
        }
        pCum /= this._N_global;
        return UtilSP.W2dBm(pCum);
    } 
    public clone(w:Waveform2){
        // Make this Waveform2 a clone of w
        this._N_global = w.N_global;
        this._Fc_global = w.Fc_global;
        this._Fstep_global = w.Fstep_global;
        this._powerSpectrumOnly = w.powerSpectrumOnly;
        this._impedance = w.impedance;
       
        this.zeroWaveform();
        // Preseve the validity flags:
        this._freqDomainComplexValid = w.freqDomainComplexValid;
        this._timeDomainComplexValid = w.timeDomainComplexValid;
        this._freqDomainPwrPhaseValid = w.freqDomainPwrPhaseValid;
        this._timeDomainPwrPhaseValid = w.timeDomainPwrPhaseValid;
 
        // Copy over all the data, whatever it is:
        let i:number;
        for (i=0; i<this._N_global; i++){
            //this._freqDomainComplex[i] = w.freqDomainComplex[i];  // This can corrupt w; instead, do this:
            this._freqDomainComplex[i].re = w.freqDomainComplex[i].re;
            this._freqDomainComplex[i].im = w.freqDomainComplex[i].im;
            //this._timeDomainComplex[i] = w.timeDomainComplex[i];  // This can corrupt w; instead, do this:
            this._timeDomainComplex[i].re = w.timeDomainComplex[i].re;
            this._timeDomainComplex[i].im = w.timeDomainComplex[i].im;
            this._freqDomainPwrdBm[i] = w.freqDomainPwrdBm[i];
            this._freqDomainPhaseDeg[i] = w.freqDomainPhaseDeg[i];
            this._timeDomainPwrdBm[i] = w.timeDomainPwrdBm[i];
            this._timeDomainPhaseDeg[i] = w.timeDomainPhaseDeg[i];
        }   
    }
    public zeroWaveform():void{
        let i:number;

        this._freqDomainComplex = new Array<Complex>(this._N_global);
        this._timeDomainComplex = new Array<Complex>(this._N_global);

        this._freqDomainPwrdBm = new Array<number>(this._N_global);
        this._freqDomainPhaseDeg = new Array<number>(this._N_global);
        this._timeDomainPwrdBm = new Array<number>(this._N_global);
        this._timeDomainPhaseDeg = new Array<number>(this._N_global);
     
        for (i=0; i<this._N_global; i++){
            this._freqDomainComplex[i] = Complex.make(0,0);
            this._timeDomainComplex[i] = Complex.make(0,0);
            this._freqDomainPwrdBm[i] = -1e6;
            this._freqDomainPhaseDeg[i] = 0;
            this._timeDomainPwrdBm[i] = -1e6;
            this._timeDomainPhaseDeg[i] = 0;
        }       
        this.everythingIsValid();
    }
    public addGain( gaindB:number, units:string){
        let i:number;
        if (units != "dB"){
            console.log("ERROR in Waveform2.addGain: units must be dB");
            return;
        }
        let g:number = UtilSP.dB2Vratio( gaindB );
 
        if (this._powerSpectrumOnly){
            for (i=0; i<this._N_global; i++){
                this._freqDomainPwrdBm[i] += gaindB;
            }
            this.everythingIsInvalid();
            this._freqDomainPwrPhaseValid = true;
        } else {
            if ( this._freqDomainComplexValid){
                for (i=0; i<this._N_global; i++){
                    //this._freqDomainComplex[i].mulC(g);
                    this._freqDomainComplex[i].re *= g;
                    this._freqDomainComplex[i].im *= g;
                }
                this.everythingIsInvalid();
                this._freqDomainComplexValid = true;
            } else if (this._timeDomainComplexValid){
                for (i=0; i<this._N_global; i++){
                    //this._timeDomainComplex[i].mulC(g);
                    this._timeDomainComplex[i].re *= g;
                    this._timeDomainComplex[i].im *= g;
                }  
                this.everythingIsInvalid();
                this._timeDomainComplexValid = true;             
            } else {
                console.log("ERROR in Waveform2.addGain: _powerSpectrumOnly = false but freq and domain complex are both invalid.");
            }
        }
    }
    public applyLimiter( gain_dB:number, Psat_dBm:number){
        if (this._powerSpectrumOnly){
            console.log("ERROR in Waveform2: cannot use applyLimiter because _powerSpectrumOnly = true.");
        } else {
            this.computeAll();  // Makes sure we have valid time domain pwr/phase
            let i:number;
            let phase0_deg:number;
            let oldPwr:number;
            let newPwr:number;

            for (i=0; i<this._N_global; i++){
                phase0_deg = this._timeDomainPhaseDeg[i];
                oldPwr = this.timeDomainPwrdBm[i];
                newPwr = Math.min( oldPwr + gain_dB, Psat_dBm) ;
                this._timeDomainPwrdBm[i] = newPwr;
                this._timeDomainPhaseDeg[i] = phase0_deg;
            }
            this.everythingIsInvalid();
            this._timeDomainPwrPhaseValid = true; 
            this.computeAll(); 
        }   
    }
    public applyThirdOrderSaturating( gain_dB:number, P1dB_dBm:number){
        if (this._powerSpectrumOnly){
            console.log("ERROR in Waveform2: cannot use applyLimiter because _powerSpectrumOnly = true.");
        } else {
            this.computeAll();  // Makes sure we have valid time domain pwr/phase
            let i:number;
            let oldPhaseDeg:number;
            let newPhaseDeg:number;
            let oldPwr:number;
            let newPwr:number;

            for (i=0; i<this._N_global; i++){
                oldPhaseDeg = this._timeDomainPhaseDeg[i];
                oldPwr = this.timeDomainPwrdBm[i];
                newPwr = Distortion.ThirdOrderSaturating(oldPwr, gain_dB, P1dB_dBm);
                newPhaseDeg = oldPhaseDeg;
                this._timeDomainPwrdBm[i] = newPwr;
                this._timeDomainPhaseDeg[i] = newPhaseDeg;
            }
            this.everythingIsInvalid();
            this._timeDomainPwrPhaseValid = true; 
            this.computeAll(); 
        }  

    }
    public applyHPA3Distortion(ss_gain:number, psat_dbm:number){
         if (this._powerSpectrumOnly){
            console.log("ERROR in Waveform2: cannot use applyHPA3Distortion because _powerSpectrumOnly = true.");
         } else {
            this.computeAll();
            let i:number;
            let pin_dBm:number;
            let phase0_deg:number;
            let pout:number;
            let lsg_dB:number;
            let phaseShift_deg:number;
            for (i=0; i<this._N_global; i++){
                pin_dBm = this._timeDomainPwrdBm[i];
                phase0_deg = this._timeDomainPhaseDeg[i];

                lsg_dB = Distortion.HPA_model3(pin_dBm,
                    "gain", 
                    ss_gain, 
                    psat_dbm, 
                    this._HPA3_softness, 
                    this._HPA3_IBO_corner_dB,
		            this._HPA3_gainBumpHeight_dB, 
                    this._HPA3_gainBumpWidth_dB, 
                    this._HPA3_IBOgainBump_dB, 
                    this._HPA3_gainFoldover, 
		            this._HPA3_phaseMax_deg, 
                    this._HPA3_IBOphasePeak_dB, 
                    this._HPA3_phaseWidth_dB, 
                    this._HPA3_phaseShape);

                phaseShift_deg = Distortion.HPA_model3(pin_dBm,
                    "gain", 
                    ss_gain, 
                    psat_dbm, 
                    this._HPA3_softness, 
                    this._HPA3_IBO_corner_dB,
		            this._HPA3_gainBumpHeight_dB, 
                    this._HPA3_gainBumpWidth_dB, 
                    this._HPA3_IBOgainBump_dB, 
                    this._HPA3_gainFoldover, 
		            this._HPA3_phaseMax_deg, 
                    this._HPA3_IBOphasePeak_dB, 
                    this._HPA3_phaseWidth_dB, 
                    this._HPA3_phaseShape); 

                this._timeDomainPwrdBm[i] += lsg_dB;
                this._timeDomainPhaseDeg[i] += phaseShift_deg;
                //console.log("pin_dBm = "+pin_dBm+", lsg_dB = "+lsg_dB)

            }
            this.everythingIsInvalid();
            this._timeDomainPwrPhaseValid = true; 
            this.computeAll(); 
            //debugger             
         }
   
    }
    public applyScalarFilter( filterdB:Array<number>){
        // Filter must be in increasing freq order
        let i:number;
        let g:number;


        if (this._powerSpectrumOnly){
            for (i=0; i<this._N_global; i++){
                this._freqDomainPwrdBm[i] += filterdB[i];
            }
            this._freqDomainPwrPhaseValid = true;
        } else {
            if ( this._freqDomainComplexValid){
                for (i=0; i<this._N_global; i++){
                    g = UtilSP.dB2Vratio( filterdB[i] );
                    //this._freqDomainComplex[i].mulC(g);
                    this._freqDomainComplex[i].re *= g;
                    this._freqDomainComplex[i].im *= g;
                }
                this.everythingIsInvalid();
                this._freqDomainComplexValid = true;
                this.convertFreqComplex2PowerPhase(); // Generate freq power/phase
                this._freqDomainPwrPhaseValid = true;
            } else {
                console.log("ERROR in Waveform2.applyScalarFilter: _freqDomainComplexValid = false ");
            }
        }
    }    

    public setToConstant( valRe:number, valIm:number, mode:string){
        var i:number;
        switch (mode){
            case "freqDomainIQ":
                for (i=0; i<this._N_global; i++){
                    this._freqDomainComplex[i].re = valRe;
                    this._freqDomainComplex[i].im = valIm;                    
                }
                this._freqDomainComplexValid = true;
                this._timeDomainComplexValid = false;
                this._freqDomainPwrPhaseValid = false;
                this._timeDomainPwrPhaseValid = false;
                break;
            case "timeDomainIQ":
                for (i=0; i<this._N_global; i++){
                    this._timeDomainComplex[i].re = valRe;
                    this._timeDomainComplex[i].im = valIm;    
                    //console.log("In setToConstant, x = "+this._timeDomainComplex[i].re);              
                }
                this._freqDomainComplexValid = false;
                this._timeDomainComplexValid = true;
                this._freqDomainPwrPhaseValid = false;
                this._timeDomainPwrPhaseValid = false;
                break;
            case "timeDomainPwrPhase":
                console.log("ERROR in Waveform1.setToConstantGain: timeDomainPwrPhase mode not implemented.");
                break;
            case "freqDomainPwrPhase":
                console.log("ERROR in Waveform1.setToConstantGain: freqDomainPwrPhase mode not implemented.");
                break;
            default:
                console.log("ERROR in Waveform2.setToConstant: mode must be freqDomainIQ or timeDomainIQ")
                break;

        }
    }

    public addWaveform(w:Waveform2){
        //console.log("In addWaveform");
        var i:number;
        if ( this._N_global != w.N_global || this._Fc_global != w.Fc_global || this._Fstep_global != w.Fstep_global){
            console.log("ERROR in Waveform2.addWaveform: Parameter mismatch:"+
            "\nthis._N_global ="+ this._N_global+", w.N_global = "+ w.N_global+
            "\nthis._Fc_global = "+this._Fc_global+", w.Fc_global = "+w.Fc_global+
            "\nthis._Fstep_global = "+this._Fstep_global+", w.Fstep_global = "+w.Fstep_global+
            "\nAborting.");
            debugger
            return;
        }
         if (this._powerSpectrumOnly){
            //// Do a power combination of this.freqDomainPwrdBm with w.freqDomainPwrdBm
            console.log("In Waveform2.ts, _powerSpectrumOnly = true, MISSING CODE TO COMBINE POWERS!");
            this.everythingIsInvalid();
            this._freqDomainPwrPhaseValid = true;
        } else {
            if (w._freqDomainComplexValid && this._freqDomainComplexValid){
                // Assume we combine freq domain complex.
                // combine freq complexes
                for (i=0; i<this._N_global; i++){
                    this._freqDomainComplex[i].re += w.freqDomainComplex[i].re;
                    this._freqDomainComplex[i].im += w.freqDomainComplex[i].im;
                }
                this.everythingIsInvalid();
                this._freqDomainComplexValid = true;
            } else {
                console.log("ERROR in Waveform2.addWaveform - can only combine valid complex freq spectra."+
                "this._freqDomainComplexValid = "+this._freqDomainComplexValid+
                ", w._freqDomainComplexValid = "+w._freqDomainComplexValid);
            }
        } 
        /*
        console.log("Exiting addWaveform:"+
        "\n  _timeDomainComplexValid = "+this._timeDomainComplexValid+
        "\n  _freqDomainComplexValid = "+this._freqDomainComplexValid+
        "\n  _timeDomainPwrPhaseValid = "+this._timeDomainPwrPhaseValid+
        "\n  _freqDomainPwrPhaseValid = "+this._freqDomainPwrPhaseValid   );  
        */   
    }
    public addPowerArrayW(a:Array<number>){
        // Adds an array of pwer in Watts to _freqDomainPwrdBml
        console.log("In addPowerArrayW");
        this.everythingIsInvalid();
        let i:number;
        let p:number;
        for (i=0; i<this._N_global; i++){
            p = UtilSP.dBm2W(this._freqDomainPwrdBm[i]);
            this._freqDomainPwrdBm[i] = UtilSP.W2dBm( p + a[i]);
        }
        this._freqDomainPwrPhaseValid = true;
    }

    public addNoise(val:number, units:string, mode:string){
        var noisePowerDensitydBmHz:number;
        var PSD:number;
        var Ptot:number;
        var span:number;
        var PinBW:number;
        var noise_sigma:number;
        var T:number;
        var F:number;
        var k:number = 1.38062E-23; // Botzmann's constant
        
        switch (units){
            case "noisePowerDensitydBmHz":
                noisePowerDensitydBmHz = val;
                break;
            case "NFdB":
                F = UtilSP.dBW2W(val);
                T = 290 * (F-1);
                noisePowerDensitydBmHz = UtilSP.W2dBW( k * T ) + 30;
                break;
            case "noiseTempK":
                T = val;
                noisePowerDensitydBmHz = UtilSP.W2dBW( k * T ) + 30;
                break;
            default:
                console.log("ERROR in Waveform2.addNoise: invalid units "+units);
                break;
        }
        PSD = Math.pow(10, (noisePowerDensitydBmHz - 30)/10); // Convert to Watts/Hz
        span = this._Fstep_global * this._N_global; // Total BW in Hz
        Ptot = PSD * span; // Total power in Watts in the entire spectral span
        PinBW = Ptot/this._N_global; // Power in Watts within one frequency step
        //noise_variance = PinBW * 50; //
        noise_sigma = Math.sqrt( PinBW * this._impedance / 2);
        //console.log("noise_sigma = "+noise_sigma);
        var i:number;
        switch (mode){
            case "freqDomain":
                if (!this._freqDomainComplexValid){
                    // Create from scratch
                    for (i=0; i<this._N_global; i++){
                        this._freqDomainComplex[i].re = noise_sigma * UtilSP.randGaussian();
                        this._freqDomainComplex[i].im = noise_sigma * UtilSP.randGaussian();
                    }
                } else {
                    // Add to what's already there.
                    for (i=0; i<this._N_global; i++){
                        this._freqDomainComplex[i].re += noise_sigma * UtilSP.randGaussian();
                        this._freqDomainComplex[i].im += noise_sigma * UtilSP.randGaussian();
                    }
                }
                this._freqDomainComplexValid = true;
                this._timeDomainComplexValid = false;
                this._freqDomainPwrPhaseValid = false;
                this._timeDomainPwrPhaseValid = false;
                break;
            case "timeDomainIQ":
                console.log("ERROR in Waveform2.addNoise: timeDomainIQ mode not implemented.");
                break;
            case "timeDomainPwrPhase":
                console.log("ERROR in Waveform2.addNoise: timeDomainPwrPhase mode not implemented.");
                break;
            case "freqDomainPwrPhase":
                console.log("ERROR in Waveform2.addNoise: freqDomainPwrPhase mode not implemented.");
                break;
        }
    }		


    public set freqDomainComplex(val:Array<Complex>){
        if (val.length != this._N_global){
            console.log("ERROR in Waveform2 set freqDomainComplex!  length of argument vector ("+val.length+") does not match N ("+this._N_global+").");
        }
        this._freqDomainComplex = val;
        this.everythingIsInvalid();
        this._freqDomainComplexValid = true;
    }
    public set timeDomainComplex(val:Array<Complex>){
        if (val.length != this._N_global){
            console.log("ERROR in Waveform2 set timeDomainComplex!  length of argument vector ("+val.length+") does not match N ("+this._N_global+").");
        }
        this._timeDomainComplex = val;
        this.everythingIsInvalid();
        this._timeDomainComplexValid = true;
    }    
    public get freqDomainComplex(){
        return this._freqDomainComplex;
    }

    public get freqDomainPwrdBm():Array<number>{
        return this._freqDomainPwrdBm;
    }
    public get freqDomainPhaseDeg():Array<number>{
        return this._freqDomainPhaseDeg;
    }   
    public get timeDomainPwrdBm():Array<number>{
        return this._timeDomainPwrdBm;
    }
    public getTimeDomainPointPwrdBm(i:number){
        return this._timeDomainPwrdBm[i];
    }    
    public get timeDomainPhaseDeg():Array<number>{
        return this._timeDomainPhaseDeg;
    }   

    public get timeDomainComplex(){
        return this._timeDomainComplex;
    }
    public setFreqDomainPwrdBmPoint(i:number, val:number){
        this._freqDomainPwrdBm[i] = val;
    }
    public setFreqDomainComplexPointRe(i:number, val:number){
        this._freqDomainComplex[i].re = val;
    }
    public setFreqDomainComplexPointIm(i:number, val:number){
        this._freqDomainComplex[i].im = val;
    }
    public setFreqDomainComplexPointC(i:number, val:Complex){
        this._freqDomainComplex[i] = val;
    }

    public setTimeDomainComplexPointRe(i:number, val:number){
        this._timeDomainComplex[i].re = val;
    }
    public setTimeDomainComplexPointIm(i:number, val:number){
        this._timeDomainComplex[i].im = val;
    }
   
    public get freqDomainRe():Array<number>{
        let ret:Array<number> = [];
        for (let i:number=0; i<this._N_global; i++){
            ret[i] = this._freqDomainComplex[i].re;
        }
        return ret;
    }
    public get freqDomainIm():Array<number>{
        let ret:Array<number> = [];
        for (let i:number=0; i<this._N_global; i++){
            ret[i] = this._freqDomainComplex[i].im;
        }
        return ret;
    }    
    public get timeDomainRe():Array<number>{
        let ret:Array<number> = [];
        for (let i:number=0; i<this._N_global; i++){
            ret[i] = this._timeDomainComplex[i].re;
        }
        return ret;
    }
    public getTimeDomainPointRe(i:number){
        return this._timeDomainComplex[i].re
    }
    public getTimeDomainPointIm(i:number){
        return this._timeDomainComplex[i].im
    }    
    public get timeDomainIm():Array<number>{
        let ret:Array<number> = [];
        for (let i:number=0; i<this._N_global; i++){
            ret[i] = this._timeDomainComplex[i].im;
        }
        return ret;
    }

    public printWaveform(){
        console.log("PRINTING WAVEFORM: _N_global = "+this._N_global+", _Fc_global = "+this._Fc_global+", _Fstep_global = "+this._Fstep_global);
        console.log("      |                  Freq                                   |                  Time                 |");
        console.log("  i   |        f              re        im       pwr     phase  |     re        im       pwr     phase  |");
        for (let i:number=0; i<this._N_global; i++){
            console.log( 
                UtilSP.formatDecimals(i,0,4)+"  |"+
                UtilSP.formatDecimals(this._Fc_global + SigProc.fftI2F_ifo( i, this._N_global, this._Fstep_global ),1,15)+"  "+
                UtilSP.formatDecimals(this._freqDomainComplex[i].re,3,8)+"  "+
                UtilSP.formatDecimals(this._freqDomainComplex[i].im,3,8) +"  "+
                UtilSP.formatDecimals(this._freqDomainPwrdBm[i],3,8) +"  "+
                UtilSP.formatDecimals(this._freqDomainPhaseDeg[i],3,8)+"  |"+
                UtilSP.formatDecimals(this._timeDomainComplex[i].re,3,8)+"  "+
                UtilSP.formatDecimals(+this._timeDomainComplex[i].im,3,8)+"  "+
                UtilSP.formatDecimals(this._timeDomainPwrdBm[i],3,8) +"  "+
                UtilSP.formatDecimals(this._timeDomainPhaseDeg[i],3,8) +" |" );
        }  
        console.log(""+
        "\n  _timeDomainComplexValid = "+this._timeDomainComplexValid+
        "\n  _freqDomainComplexValid = "+this._freqDomainComplexValid+
        "\n  _timeDomainPwrPhaseValid = "+this._timeDomainPwrPhaseValid+
        "\n  _freqDomainPwrPhaseValid = "+this._freqDomainPwrPhaseValid   );            
        console.log("-------------");
    }


    //----------------------------------------
    //Conversions:
    public convertTimeComplex2FreqComplex(){
        if (!this._timeDomainComplexValid){
            console.log("ERROR in Waveform2.convertTimeComplex2FreqComplex. _timeDomainComplexValid = false.  Cannot do FFT."); 
            return;
        }
        let i:number;
        let xx:Array<number> = [];
        let yy:Array<number> = [];
        for (i = 0; i<this._N_global; i++){
            xx[i] = this._timeDomainComplex[i].re;
            yy[i] = this._timeDomainComplex[i].im;
        }      
        SigProc.fftCore( xx, yy, false); // Does the FFT in place on xx and yy
        //Scale by this._N_global so that total input power (both sides of spectrum) = average output power
        for (i = 0; i<this._N_global; i++){
            xx[i] /= this._N_global;
            yy[i] /= this._N_global;
        }

        // Swap back to increasing frequency order:
        xx = SigProc.fftSwapAN(xx);
        yy = SigProc.fftSwapAN(yy);

        for (i = 0; i<this._N_global; i++){
            this._freqDomainComplex[i].re = xx[i];
            this._freqDomainComplex[i].im = yy[i]
        }

        this._freqDomainComplexValid = true;
        this._freqDomainPwrPhaseValid = false;

    }
    public convertFreqComplex2TimeComplex(){
        if (!this._freqDomainComplex){
            console.log("ERROR in Waveform2.convertFreqComplex2TimeComplex. _freqDomainComplex = false.  Cannot do inverse FFT."); 
            return;
        }
        let i:number;
        let xx:Array<number> = [];
        let yy:Array<number> = [];
        for (i = 0; i<this._N_global; i++){
            xx[i] = this._freqDomainComplex[i].re;
            yy[i] = this._freqDomainComplex[i].im;
        }
        // Swap to FFT order: 
        xx = SigProc.fftInverseSwapAN(xx);
        yy = SigProc.fftInverseSwapAN(yy);
        //console.log("xx after swap: = "+xx);     
        SigProc.fftCore( xx, yy, true); // Inverse FFT
        //Scale by this._N_global so that total input power (both sides of spectrum) = average output power
        for (i = 0; i<this._N_global; i++){
            this._timeDomainComplex[i].re = xx[i] * this._N_global;
            this._timeDomainComplex[i].im = yy[i] * this._N_global;            
        }
        this._timeDomainComplexValid = true;
        this._timeDomainPwrPhaseValid = false;

        //console.log("xx after inv fft: = "+xx);
        
    }
    public get status(){
        return(
        "f Cmplx ="+this._freqDomainComplexValid+
        ", f PwrPh ="+this._freqDomainPwrPhaseValid+
        ", t Cmplx ="+this._timeDomainComplexValid+
        ", t PwrPh="+this._timeDomainPwrPhaseValid);
    }
    public convertTimeComplex2PowerPhase(){
        if (!this._timeDomainComplexValid){
            console.log("ERROR in Waveform2.convertTimeComplex2PowerPhase.  _timeDomainComplexValid = false.  Cannot convert to power/phase."); 
            return;
        }
        let pW:number;
        let phaseRad:number;
        let i:number;
        for (i=0; i<this._N_global; i++){
            pW = (this._timeDomainComplex[i].re * this._timeDomainComplex[i].re + 
                this._timeDomainComplex[i].im * this._timeDomainComplex[i].im ) 
                / this._impedance;
            this._timeDomainPwrdBm[i] = UtilSP.W2dBm( pW );	
            
            if ( this._timeDomainComplex[i].re == 0 && this._timeDomainComplex[i].im == 0 ){
                phaseRad = 0;
            } else {
                phaseRad = Math.atan2( this._timeDomainComplex[i].im, this._timeDomainComplex[i].re );
            }
            this._timeDomainPhaseDeg[i] = UtilSP.degrees(phaseRad);
            if (i==0){
                //console.log("In convertTimeIQ2PowerPhase: "+this._timeDomainPwrdBm[i]);
            }
        }
        this._timeDomainPwrPhaseValid = true;
    }
    public convertFreqComplex2PowerPhase(){
        // generates power (dBm) and phase (deg)
        if (!this._freqDomainComplexValid){
            console.log("ERROR in Waveform2.convertFreqComplex2PowerPhase.  _freqDomainComplexValid = false.  Cannot convert to power/phase."); 
            return;
        }
        let pW:number;
        let phaseRad:number;
        let i:number;
        for (i=0; i<this._N_global; i++){
            pW = (this._freqDomainComplex[i].re * this._freqDomainComplex[i].re + 
                this._freqDomainComplex[i].im * this._freqDomainComplex[i].im ) 
                / this._impedance;
            this._freqDomainPwrdBm[i] = UtilSP.W2dBm( pW );	
            
            if ( this._freqDomainComplex[i].re == 0 && this._freqDomainComplex[i].im == 0 ){
                phaseRad = 0;
            } else {
                phaseRad = Math.atan2( this._timeDomainComplex[i].im, this._timeDomainComplex[i].re );
            }
            this._freqDomainPhaseDeg[i] = UtilSP.degrees(phaseRad);
            if (i==0){
                //console.log("In convertTimeIQ2PowerPhase: "+this._timeDomainPwrdBm[i]);
            }
        }
        this._freqDomainPwrPhaseValid = true;
        //console.log( "In convertFreqComplex2PowerPhase "+this._freqDomainPwrdBm);
    }    
    public convertTimePowerPhase2Complex(){
        if (!this.timeDomainPwrPhaseValid){
            console.log("ERROR in Waveform2.convertTimePowerPhase2Complex.  this.timeDomainPwrPhaseValid = false.  Cannot convert to I-Q."); 
            return;
        }
        var mag:number;
        var pW:number;
        var phaseRad:number;
        for (var i=0; i<this._N_global; i++){
            pW = UtilSP.dBm2W(this._timeDomainPwrdBm[i]);
            phaseRad = UtilSP.radians(this._timeDomainPhaseDeg[i]);
            mag = Math.sqrt( pW * this._impedance );
            this._timeDomainComplex[i].re = mag * Math.cos(phaseRad);
            this._timeDomainComplex[i].im = mag * Math.sin(phaseRad);
        }
        this._timeDomainComplexValid = true;
    }
    public convertFreqPowerPhase2Complex(){
        if (!this.freqDomainPwrPhaseValid){
            console.log("ERROR in Waveform2.convertFreqPowerPhase2Complex.  this.freqDomainPwrPhaseValid = false.  Cannot convert to I-Q."); 
            return;
        }
        var mag:number;
        var pW:number;
        var phaseRad:number;
        for (var i=0; i<this._N_global; i++){
            pW = UtilSP.dBm2W(this._freqDomainPwrdBm[i]);
            phaseRad = UtilSP.radians(this._freqDomainPhaseDeg[i]);
            mag = Math.sqrt( pW * this._impedance );
            this._freqDomainComplex[i].re = mag * Math.cos(phaseRad);
            this._freqDomainComplex[i].im = mag * Math.sin(phaseRad);
        }
        this._freqDomainComplexValid = true;
    }
    // Validity checks:
    public set freqDomainComplexValid(val:boolean){
        this._freqDomainComplexValid = val;
    }
    public get freqDomainComplexValid():boolean{
        return (this._freqDomainComplexValid);
    } 
    public set timeDomainComplexValid(val:boolean){
        this._timeDomainComplexValid = val;
    }       
    public get timeDomainComplexValid(){
        return (this._timeDomainComplexValid);
    }  
    public set freqDomainPwrPhaseValid(val:boolean){
        this._freqDomainPwrPhaseValid = val;
    }  
    public get freqDomainPwrPhaseValid():boolean{
        return (this._freqDomainPwrPhaseValid);
    }      
    public get timeDomainPwrPhaseValid(){
        return (this._timeDomainPwrPhaseValid);
    }
    

    public everythingIsInvalid(){
        this._freqDomainComplexValid = false;
        this._timeDomainComplexValid = false;
        this._freqDomainPwrPhaseValid = false;
        this._timeDomainPwrPhaseValid = false;
    }
    public everythingIsValid(){
        this._freqDomainComplexValid = true;
        this._timeDomainComplexValid = true;
        this._freqDomainPwrPhaseValid = true;
        this._timeDomainPwrPhaseValid = true;
    }    
    public set systemParameters(p:any){
        this.powerSpectrumOnly = p.powerSpectrumOnly;
        this._N_global = p.N_global;
        this.Fstep_global = p.Fstep_global;
        this.Fc_global = p.Fc_global;
        this._impedance = p.impedance;
    }
    public set impedance(val:number){
        this._impedance = val;
    }
    public get impedance(){
        return this._impedance;
    }
    public set N_global(val:number){
        if (val == this._N_global){
            return;
        }
        this._N_global = val;
        this.zeroWaveform();
    }
    public get N_global(){
        return this._N_global;
    }
    public set Fc_global(val:number){
        this._Fc_global = val;
    }
    public get Fc_global(){
        return this._Fc_global;
    }
    public set Fstep_global(val:number){
        this._Fstep_global = val;
    }
    public get Fstep_global(){
        return this._Fstep_global;
    }
    public set powerSpectrumOnly(val:boolean){
        this._powerSpectrumOnly = val;
    }
    public get powerSpectrumOnly():boolean{
        return this._powerSpectrumOnly;
    }
    public presetHPA3distortionParameters(val:string){
        switch (val){
            case "SSPA":
                this._distortionShape = val;
				this._HPA3_softness = 0.8; //softness of the compression curve.  0 = ideal limiter, 1.5 = typical TWT. 
				this._HPA3_IBO_corner_dB = 0;
				this._HPA3_gainBumpHeight_dB = 0.5;
				this._HPA3_gainBumpWidth_dB = 3;
				this._HPA3_IBOgainBump_dB = -4;
				this._HPA3_gainFoldover = 0; 
				this._HPA3_phaseMax_deg = 33;
				this._HPA3_IBOphasePeak_dB = 8.1;
				this._HPA3_phaseWidth_dB = 10;
				this._HPA3_phaseShape = 2;    
                break;
            case "TWTA":
                this._distortionShape = val;
				this._HPA3_softness = 1.5;
				this._HPA3_IBO_corner_dB = 0;
				this._HPA3_gainBumpHeight_dB = 0;
				this._HPA3_gainBumpWidth_dB = 3;
				this._HPA3_IBOgainBump_dB = -4;
				this._HPA3_gainFoldover = 1.1; 
				this._HPA3_phaseMax_deg = 33;
				this._HPA3_IBOphasePeak_dB = 8.1;
				this._HPA3_phaseWidth_dB = 10;
				this._HPA3_phaseShape = 1.7;
                break;
            default:
                console.log("ERROR in Waveform2: invalid presetHPA3distortionParameters value: "+val)
                break;         
        
        }
    }

} // End class